// Выбор одного значения с поиском (ux.md, раздел 5): кнопка в виде поля, под ней панель
// с полем поиска и списком; фокус — в поле поиска, текущее значение отмечено галочкой.
// Для выбора человека из многих: заместитель, чья роль. Поиск выполняет потребитель
// (на сервере), варианты приходят в options. Подпись, подсказка и ошибка — как у Field.
// Вариант, выбранный клавишами, обведён, как фокус; под указателем — только фон (useListNav).
// «Поиск…» — только если поиск идёт дольше 0,3 с (ux.md, раздел 14: загрузка не мигает).
import * as RadixPopover from '@radix-ui/react-popover';
import { Command } from 'cmdk';
import { Check, ChevronDown, Search } from 'lucide-react';
import { useRef, useState } from 'react';
import type { Ref } from 'react';
import { cx } from '../lib/cx.ts';
import { Icon } from '../lib/icon.tsx';
import { relayEscape } from '../lib/layers.ts';
import { useListNav } from '../lib/list-nav.ts';
import { useExitFocus } from '../lib/return-focus.ts';
import { useDelayedFlag } from '../lib/use-delayed.ts';
import { Field } from './Field.tsx';
import type { FieldProps } from './Field.tsx';

export interface SearchPickerOption {
  value: string;
  label: string;
  /** Пояснение под названием: почта пользователя. */
  description?: string;
}

export interface SearchPickerFieldProps extends Omit<FieldProps, 'children'> {
  value: SearchPickerOption | null;
  onChange: (option: SearchPickerOption) => void;
  /** Строка поиска; варианты по ней — в options. */
  query: string;
  onQueryChange: (query: string) => void;
  options: readonly SearchPickerOption[];
  /** Идёт поиск: «Поиск…» появится, если ответа нет дольше 0,3 с. */
  loading?: boolean;
  /** Пустое значение приглашает к выбору: «Выберите пользователя». */
  placeholder?: string;
  /** Пример ввода в поле поиска: «Найти по ФИО или почте». */
  searchPlaceholder?: string;
  /** Поиск ничего не дал: «Никого не нашлось». */
  emptyText?: string;
  /** Первое поле окна: фокус при открытии окна — на эту кнопку (Modal ищет data-autofocus). */
  autoFocus?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

export function SearchPickerField({
  value,
  onChange,
  query,
  onQueryChange,
  options,
  loading = false,
  placeholder = 'Выбрать',
  searchPlaceholder = 'Найти',
  emptyText = 'Ничего не найдено',
  autoFocus = false,
  ref,
  ...field
}: SearchPickerFieldProps) {
  const [open, setOpen] = useState(false);
  const listNav = useListNav(open);
  const searching = useDelayedFlag(loading);
  const label = typeof field.label === 'string' ? field.label : undefined;
  // Фокус на кнопку — в начале выхода панели, а не после него (044, MOT-06).
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const exitFocus = useExitFocus(open, { triggerRef, contentRef });
  return (
    <Field {...field}>
      {(control) => {
        const valueId = `${control.id}-value`;
        return (
          // Панель модальная: прокрутка списка работает и внутри окна, Esc закрывает только её.
          <RadixPopover.Root open={open} onOpenChange={setOpen} modal>
            <RadixPopover.Trigger asChild ref={triggerRef}>
              <button
                ref={ref}
                type="button"
                id={control.id}
                // Имя кнопки — подпись и текущее значение: «Заместитель Рябов Р. Р.».
                aria-labelledby={`${control.id}-label ${valueId}`}
                aria-describedby={control['aria-describedby']}
                disabled={control.disabled}
                data-autofocus={autoFocus || undefined}
                className={cx(
                  't-input kp-picker',
                  !value && 'is-empty',
                  control['aria-invalid'] && 'is-error',
                )}
              >
                <span id={valueId} className="kp-picker-value">
                  {value?.label ?? placeholder}
                </span>
                <Icon icon={ChevronDown} className="t-ico" />
              </button>
            </RadixPopover.Trigger>
            <RadixPopover.Portal>
              <RadixPopover.Content
                ref={contentRef}
                // Панель модальная: как у Radix, фокус на кнопку и после нажатия снаружи.
                onCloseAutoFocus={exitFocus.onCloseAutoFocus}
                // Esc, пока панель уходит, — следующему слою (044, MOT-07).
                onEscapeKeyDown={(event) => {
                  if (open) return;
                  event.preventDefault();
                  relayEscape(contentRef.current, event);
                }}
                aria-label={label}
                align="start"
                sideOffset={4}
                collisionPadding={8}
                className="t-pop kp-pop kp-search-pop"
                data-nav={listNav.nav}
              >
                <Command
                  label={label}
                  shouldFilter={false}
                  loop
                  defaultValue={value?.value}
                  onKeyDown={listNav.onKeyDown}
                >
                  <div className="t-pop-search kp-search-pop-in">
                    <Icon icon={Search} className="t-ico" />
                    <Command.Input
                      className="kp-search-pop-input"
                      value={query}
                      onValueChange={onQueryChange}
                      placeholder={searchPlaceholder}
                    />
                  </div>
                  <Command.List
                    className="kp-search-pop-list"
                    label={label}
                    onPointerMove={listNav.onPointerMove}
                  >
                    {options.map((option) => (
                      <Command.Item
                        key={option.value}
                        value={option.value}
                        onSelect={() => {
                          onChange(option);
                          setOpen(false);
                        }}
                        className="t-mi kp-mi-2 kp-search-item"
                      >
                        <span className="kp-mi-text">
                          <span>{option.label}</span>
                          {option.description ? (
                            <span className="kp-mi-desc">{option.description}</span>
                          ) : null}
                        </span>
                        {option.value === value?.value ? (
                          <span className="ck" aria-hidden="true">
                            <Icon icon={Check} />
                          </span>
                        ) : null}
                      </Command.Item>
                    ))}
                  </Command.List>
                  <div className="kp-search-pop-status" role="status">
                    {searching ? <p className="kp-pal-note">Поиск…</p> : null}
                    {!loading && options.length === 0 ? (
                      <p className="kp-pal-note">{emptyText}</p>
                    ) : null}
                  </div>
                </Command>
              </RadixPopover.Content>
            </RadixPopover.Portal>
          </RadixPopover.Root>
        );
      }}
    </Field>
  );
}
